import axios, { Canceler } from 'axios';
import { orderBy, keyBy } from 'lodash';
import React from 'react';
import { connect, DispatchProp } from 'react-redux';

import {
    Collapsible,
    CollapsibleHeaderContent,
    CollapsibleHeading,
    LoadingPlaceholder,
} from '@dndbeyond/character-components/es';
import {
    ApiAdapterPromise,
    ApiAdapterRequestConfig,
    ApiAdapterUtils,
    ApiResponse,
    characterActions,
    CharacterPreferences,
    CharacterTheme,
    DataOrigin,
    Feat,
    FeatDefinitionContract,
    FeatUtils,
    HelperUtils,
    PrerequisiteData,
    PrerequisiteUtils,
    rulesEngineSelectors,
} from '@dndbeyond/character-rules-engine/es';

import { sidebarActions } from '../../../actions';
import { SidebarHeader } from '../../../components/Sidebar';
import DataLoadingStatusEnum from '../../../constants/DataLoadingStatusEnum';
import { apiCreatorSelectors } from '../../../selectors';
import { SharedAppState } from '../../../stores/typings';
import { AppLoggerUtils, PaneComponentEnum, PaneUtils } from '../../../utils';
import FeatsManagePaneFeat from './FeatsManagePaneFeat';

interface Props extends DispatchProp {
    feats: Array<Feat>;
    loadAvailableFeats: (
        additionalConfig?: Partial<ApiAdapterRequestConfig>,
    ) => ApiAdapterPromise<ApiResponse<Array<FeatDefinitionContract>>>;
    prerequisiteData: PrerequisiteData;
    preferences: CharacterPreferences;
    theme: CharacterTheme;
}
interface State {
    featData: Array<Feat>;
    loadingStatus: DataLoadingStatusEnum;
}
class FeatsManagePane extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            featData: [],
            loadingStatus: DataLoadingStatusEnum.NOT_INITIALIZED,
        };
    }

    loadFeatsCanceler: null | Canceler = null;

    componentDidMount(): void {
        const { loadAvailableFeats } = this.props;

        this.setState({
            loadingStatus: DataLoadingStatusEnum.LOADING,
        });

        loadAvailableFeats({
            cancelToken: new axios.CancelToken((c) => {
                this.loadFeatsCanceler = c;
            }),
        })
            .then((response) => {
                let featData: Array<Feat> = [];

                const data = ApiAdapterUtils.getResponseData(response);
                if (data !== null) {
                    featData = data.map((definition) => FeatUtils.simulateFeat(definition));
                }

                this.setState({
                    featData,
                    loadingStatus: DataLoadingStatusEnum.LOADED,
                });
                this.loadFeatsCanceler = null;
            })
            .catch(AppLoggerUtils.handleAdhocApiError);
    }

    componentWillUnmount(): void {
        if (this.loadFeatsCanceler !== null) {
            this.loadFeatsCanceler();
        }
    }

    handleFeatCreate = (feat: Feat): void => {
        const { dispatch } = this.props;

        dispatch(characterActions.adhocFeatCreate(FeatUtils.getId(feat)));
    };

    handleFeatRemove = (feat: Feat): void => {
        const { dispatch } = this.props;

        dispatch(characterActions.adhocFeatRemove(FeatUtils.getId(feat)));
    };

    handleDataOriginClick = (dataOrigin: DataOrigin): void => {
        const { dispatch } = this.props;

        let component = PaneUtils.getDataOriginComponentInfo(dataOrigin);
        if (component.type !== PaneComponentEnum.ERROR_404) {
            dispatch(sidebarActions.paneHistoryPush(component.type, component.identifiers));
        }
    };

    renderContent = (): React.ReactNode => {
        const { feats, prerequisiteData, preferences, theme } = this.props;
        const { featData } = this.state;

        let existingFeatLookup = keyBy(feats, (feat) => FeatUtils.getId(feat));
        let availableFeats: Array<Feat> = [...feats];
        let unavailableFeats: Array<Feat> = [];

        featData.forEach((feat) => {
            if (HelperUtils.lookupDataOrFallback(existingFeatLookup, FeatUtils.getId(feat))) {
                return;
            }

            if (PrerequisiteUtils.validatePrerequisiteGrouping(FeatUtils.getPrerequisites(feat), prerequisiteData)) {
                availableFeats.push(feat);
            } else {
                unavailableFeats.push(feat);
            }
        });

        let sortedAvailableFeats: Array<Feat> = orderBy(availableFeats, (feat) => FeatUtils.getName(feat));
        let sortedUnavailableFeats: Array<Feat> = orderBy(unavailableFeats, (feat) => FeatUtils.getName(feat));

        let unavailableFeatsHeading: React.ReactNode = (
            <CollapsibleHeading>
                Unavailable Feats
                <span className="ct-feats-manage-pane__unavailable-callout">Prerequisites Not Met</span>
            </CollapsibleHeading>
        );

        let unavailableFeatsHeader: React.ReactNode = <CollapsibleHeaderContent heading={unavailableFeatsHeading} />;

        const shouldRenderUnavailableFeats: boolean = sortedUnavailableFeats.length > 0;

        return (
            <React.Fragment>
                <div className="ct-feats-manage-pane__feats ct-feats-manage-pane__feats--available">
                    {sortedAvailableFeats.map((feat) => (
                        <FeatsManagePaneFeat
                            theme={theme}
                            key={FeatUtils.getId(feat)}
                            feat={feat}
                            onRemove={this.handleFeatRemove}
                            onAdd={this.handleFeatCreate}
                            onDataOriginClick={this.handleDataOriginClick}
                        />
                    ))}
                </div>
                {shouldRenderUnavailableFeats && (
                    <Collapsible
                        header={unavailableFeatsHeader}
                        className="ct-feats-manage-pane__feats ct-feats-manage-pane__feats--unavailable"
                    >
                        {sortedUnavailableFeats.map((feat) => (
                            <FeatsManagePaneFeat
                                theme={theme}
                                key={FeatUtils.getId(feat)}
                                feat={feat}
                                onRemove={this.handleFeatRemove}
                                onAdd={this.handleFeatCreate}
                                onDataOriginClick={this.handleDataOriginClick}
                                enableAdd={!preferences.enforceFeatRules}
                                showFailures={true}
                                prerequisiteData={prerequisiteData}
                            />
                        ))}
                    </Collapsible>
                )}
            </React.Fragment>
        );
    };

    render() {
        const { loadingStatus } = this.state;

        let contentNode: React.ReactNode;
        if (loadingStatus === DataLoadingStatusEnum.LOADED) {
            contentNode = this.renderContent();
        } else {
            contentNode = <LoadingPlaceholder />;
        }

        return (
            <div className="ct-feats-manage-pane">
                <SidebarHeader>Manage Feats</SidebarHeader>
                {contentNode}
            </div>
        );
    }
}

function mapStateToProps(state: SharedAppState) {
    return {
        feats: rulesEngineSelectors.getFeats(state),
        loadAvailableFeats: apiCreatorSelectors.makeLoadAvailableFeats(state),
        prerequisiteData: rulesEngineSelectors.getPrerequisiteData(state),
        preferences: rulesEngineSelectors.getCharacterPreferences(state),
        theme: rulesEngineSelectors.getCharacterTheme(state),
    };
}

export default connect(mapStateToProps)(FeatsManagePane);
